<template>
  <div class="search-input-wrapper">
    <q-input
      ref="input"
      v-model="search"
      :class="{ 'has-value': !!search }"
      class="input"
      debounce="500"
      style="width: 200px"
      dense
      outlined
      :placeholder="focus ? '' : 'search'"
      @focus="focus = menu = true"
      @blur="focus = menu = false"
      @keydown.enter="goToSearchResultsPage"
    >
      <template v-if="focus || !!search" #prepend>
        <q-icon name="search" color="white-5" size="18px" />
      </template>

      <template #append>
        <q-icon
          v-if="search !== ''"
          name="close"
          class="cursor-pointer"
          color="secondary-1"
          size="18px"
          @click="clearSearch"
        />
        <q-icon v-if="!focus && !search" name="search" color="white-1" size="18px" />
      </template>

      <q-menu
        content-class="search-input-menu"
        anchor="bottom middle"
        self="top middle"
        no-focus
        no-parent-event
        :value="menu"
        max-width="350px"
      >
        <q-list style="width: 350px">
          <template v-if="loadingOptions">
            <q-item>
              <q-item-section class="text-grey">
                <q-spinner color="primary" size="1.5em" />
              </q-item-section>
            </q-item>
          </template>

          <template v-else>
            <q-item
              v-for="option of options"
              :key="option.id + option.uuid"
              :class="option.tag_label.replaceAll(' ', '-')"
              :to="option.route"
            >
              <q-item-section>
                <q-item-label class="text-weight-medium ellipsis">{{ option.name }}</q-item-label>
                <q-item-label caption>{{ option.tag_label }}</q-item-label>
              </q-item-section>
            </q-item>

            <router-link v-if="options.length" :to="`/search-results/${search}`" class="see-more-results">
              See all results
            </router-link>

            <q-item v-if="!options.length">
              <q-item-section class="text-grey">
                No results
              </q-item-section>
            </q-item>
          </template>
        </q-list>
      </q-menu>
    </q-input>
  </div>
</template>

<script>
import * as apiTo from '@/modular-api'
import { searchResultsRouteMap } from '@/utils/search-results-route-map'

export default {
  data: () => ({
    search: '',
    options: [],
    focus: false,
    menu: false,
    loadingOptions: false
  }),

  watch: {
    search(newVal) {
      const search = newVal.trim()

      if (!search) {
        this.options = []
      } else {
        this.loadingOptions = true
        apiTo
          .GET_GLOBAL_SEARCH_TOP_RESULTS(search)
          .then(({ data }) => this.setOptions(data.result))
          .finally(() => (this.loadingOptions = false))
      }
    }
  },

  methods: {
    filterFn(value, update, abort) {
      const search = value.trim()

      if (!search) {
        update(() => (this.options = []))
      } else {
        apiTo
          .GET_GLOBAL_SEARCH_TOP_RESULTS(search)
          .then(({ data }) => update(() => this.setOptions(data.result)))
          .catch(abort)
      }
    },

    goToSearchResultsPage() {
      if (!this.search.trim()) return
      const newRoute = `/search-results/${this.search.trim()}`
      if (this.$route.path !== newRoute) this.$router.push(newRoute)
    },

    setOptions(options) {
      this.options = options.map(opt => {
        let routeId

        if (opt.tag === 'subscription_period') routeId = opt.subscription_id
        else if (['invoice_vendor', 'invoice_publisher', 'note'].includes(opt.tag)) routeId = opt.provider_id
        else routeId = opt.id

        return {
          ...opt,
          route: searchResultsRouteMap[opt.tag](routeId, opt.provider_type)
        }
      })
    },

    clearSearch() {
      this.search = ''
      this.$refs.input.focus()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins';
@import '~@/styles/general';
@import '~@/styles/fonts';

.search-input-wrapper {
  > .input {
    @include quasar-field-control-style {
      background: rgba(white, 0.5);
      @extend .border-radius-sm;
      border: 1px solid transparent;

      &:before,
      &:after {
        display: none;
      }

      input {
        &::placeholder {
          color: var(--r-secondary-1) !important;
        }

        &.q-field__native {
          @extend .text-body-1;
        }
      }

      &:hover {
        border: 1px solid white;
      }
    }

    &.has-value {
      ::v-deep .q-field__control {
        background: rgba(white, 0.9);
        border: 1px solid transparent;
        padding: 0 !important;

        input {
          color: var(--r-secondary-2) !important;
        }
      }
    }

    &.q-field--focused {
      ::v-deep .q-field__control {
        background: rgba(white, 0.9);
        border: 1px solid transparent;
        padding: 0 !important;

        input {
          @extend .text-body-1;
          color: var(--r-secondary-1) !important;
        }
      }
    }
  }
}
</style>

<style lang="stylus" scoped>
@import '~@/styles/stylus/variables.styl'

.search-input-menu {
  .q-item {
    margin 10px
    border-left 5px solid transparent
    border-radius 4px
    background $lighter-grey

    &.publisher { border-color: $accent-colour }
    &.vendor { border-color: $lm-cyan }
    &.product { border-color: $lm-light-green }
    &.subscription { border-color: $lm-green }
    &.note { border-color: var(--r-primary-1) }
    &.subscription-period-purchase-order-reference { border-color: #0a4242 }
    &.license { border-color: $primary }
    &.invoice-vendor, &.invoice-publisher { border-color: var(--r-secondary-5) }
    &.publication { border-color: $lm-indigo }
  }

  .see-more-results {
    text-decoration none
    color $accent-colour
    text-align: center;
    display: grid;
    padding-bottom: 5px;

    &:hover {
      text-decoration underline
    }
  }
}
</style>
